import React from 'react';
import PropTypes from 'prop-types';
import ImportProcess from './model';
import ImportRender from './render';
import useStateMachine from './state';

export default function ImportController({ importProcessSlug, importProcess, createImportProcess, refreshImportProcess, triggerImportProcess, onCancel, ...props }) {
    // Initialize the state machine
    const [state, transition] = useStateMachine(importProcess);

    const onFileSelected = React.useCallback(file => {
        transition('file-selected', { file });
    });

    const onFileUploaded = React.useCallback(
        key => {
            if (!key) {
                transition('cancel');
                return;
            }
            transition('file-uploaded', { key });
            createImportProcess({
                process: importProcessSlug,
                source: key
            });
        },
        [importProcessSlug, createImportProcess]
    );

    const onTrigger = React.useCallback(() => {
        triggerImportProcess(importProcess.getID(), {
            status: 'ready'
        });
        transition('import-process-confirmed');
    }, [importProcess, triggerImportProcess]);

    const onReset = React.useCallback(() => {
        transition('cancel');
        if (onCancel) {
            onCancel();
        }
    }, [onCancel]);

    // transition to import-process-changed when import process has been created
    // or when the import process has been refreshed
    React.useEffect(() => {
        if (importProcess && state.step !== 'import-process-done' && state.step !== 'import-process-confirm') {
            transition('import-process-changed', {
                importProcess,
                poll: wait => {
                    return setTimeout(() => {
                        refreshImportProcess(importProcess.getID());
                        transition('clear-timeout');
                    }, wait * 1000);
                }
            });
        }
    }, [importProcess]);

    React.useEffect(() => {
        // Return the clear timeout callback
        if (state.pollingTimeoutID) {
            return () => clearTimeout(state.pollingTimeoutID);
        }
    }, [state.pollingTimeoutID]);

    return (
        <ImportRender
            step={state.step}
            filename={(state.file && state.file.name) || ''}
            importProcess={importProcess}
            onFileSelected={onFileSelected}
            onFileUploaded={onFileUploaded}
            onTrigger={onTrigger}
            onReset={onReset}
            {...props}
        />
    );
}

ImportController.propTypes = {
    importProcessSlug: PropTypes.string.isRequired,
    createImportProcess: PropTypes.func.isRequired,
    refreshImportProcess: PropTypes.func.isRequired,
    triggerImportProcess: PropTypes.func.isRequired,
    onCancel: PropTypes.func,
    importProcess: PropTypes.instanceOf(ImportProcess)
};
